import React, { useEffect, useState, useContext } from 'react';

import { navigate } from 'gatsby';

import { Auth, Hub } from 'aws-amplify';
import { ISignUpResult } from 'amazon-cognito-identity-js';

import {
  //sendEmailVerificationCode,
  customAuthChannel,
  signout,
} from './cognitoUtils';

import {
  useUserAuthContextLazyQuery,
  UserAuthContextQuery,
  Role_Enum,
  Accreditation_Enum,
} from 'generated/graphql';

import { uuidString, Timezone } from 'utils/interface';

import { AuthEvent, CustomAuthEvent } from './authEvent';

// We define these 2 because Amplify does not currently export it https://github.com/aws-amplify/amplify-js/issues/4817
// From https://github.com/aws-amplify/amplify-js/blob/a047ce73/packages/core/src/Hub.ts#L39
type HubPayload = {
  event: string;
  data?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  message?: string;
};

type HubCapsule = {
  channel: string;
  payload: HubPayload;
  source: string;
  patternInfo?: string[];
};

export const allLocations = 'ALL'; // Used as id in selectedLocation to indicate "all locations" chosen
export type AuthContextLocation = {
  id: uuidString;
  name: string;
  timezone: Timezone;
  parentLocation: string | null;
  usesParentLocationPlans: boolean;
  providerDirectProductPurchase: boolean;
  providerDirectPurchaseProfitMargin: number | null | undefined;
  bookingAllowed: boolean;
};

export interface AuthUser {
  email: string;
  email_verified: boolean;
  phone_number: string;
  phone_number_verified: boolean;
  sub: string; // An always unique id for the user
  username: string; // Current username for the user, but at a later date another use may have
  authorized: boolean;
  authData?: ISignUpResult;
  firstName?: string;
  lastName?: string;
  role?: Role_Enum;
  accreditation?: Accreditation_Enum;
  id: string;
  locations: Array<AuthContextLocation>;
  selectedLocation: AuthContextLocation;
  setSelectedLocation: React.Dispatch<AuthContextLocation>;
}

export const defaultAuthUser: AuthUser = {
  id: undefined,
  email: '',
  email_verified: false,
  phone_number: '',
  phone_number_verified: false,
  sub: undefined,
  username: '',
  authorized: false,
  authData: undefined,
  locations: [],
  firstName: undefined,
  lastName: undefined,
  role: undefined,
  accreditation: undefined,
  setSelectedLocation: undefined,
  selectedLocation: undefined,
};

/**
 * Return extracted user from the GQL query result.
 *
 * @param user from the raw GQL query.
 */
function getFormattedUser(user: UserAuthContextQuery):
  | undefined
  | {
      firstName: string;
      lastName: string;
      role: Role_Enum;
      accreditation: Accreditation_Enum;
      id: string;
      locations: Array<AuthContextLocation>;
    } {
  if (user?.user?.length == 1) {
    const { firstName, lastName, role, userLocations, id, accreditation } =
      user.user[0];
    const locations = userLocations.map(
      ({ locationByLocation: { name, id } }) => ({
        name,
        id,
      }),
    );
    if (locations.length > 1) {
      locations.push({ name: 'All', id: allLocations });
    }
    return {
      firstName: firstName ?? '',
      lastName: lastName ?? '',
      role,
      accreditation,
      id,
      locations: userLocations.map(
        ({
          locationByLocation: {
            name,
            id,
            timezone,
            parentLocation,
            usesParentLocationPlans,
            providerDirectProductPurchase,
            providerDirectPurchaseProfitMargin,
            bookingAllowed,
          },
        }) => ({
          name,
          id,
          timezone,
          parentLocation,
          usesParentLocationPlans,
          providerDirectProductPurchase,
          providerDirectPurchaseProfitMargin,
          bookingAllowed,
        }),
      ),
    };
  } else {
    console.log(`Got length ${user.user.length} for UserAuthContextQuery`);
  }
  return undefined;
}

interface AuthContext {
  authUser: AuthUser;
  authUserPromise: () => Promise<AuthUser>;
  logout: (goHome: boolean) => Promise<void>;
  setUsername: (username: string) => void;
  refresh: () => Promise<void>;
}

const defaultAuthContext: AuthContext = {
  authUser: defaultAuthUser,
  authUserPromise: () => Promise.resolve(defaultAuthUser),
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  logout: async (goHome: boolean) => Promise.resolve(),
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setUsername: (username: string) => undefined,
  refresh: () => Promise.resolve(),
};

// AuthContext provides all authentication/access related state to the application.
// This default value really doesn't do much, odd that React requires it.
export const AuthContext = React.createContext(defaultAuthContext);

export const useAuthContext = (): AuthContext => useContext(AuthContext);

/**
 * Provider that powers the AuthContext.
 *
 * Provide all authentication/permission state related user data, by stitching Cognito information
 * with local database.
 */

type Props = {
  resetAuth: () => void;
};

export const AuthProvider: React.FC<
  Props & React.PropsWithChildren<React.ReactNode>
> = ({ resetAuth, children }) => {
  // set should not be used directly - all changes to authUser go through updateAuthUser
  const [authUser, setAuthUser] = useState(defaultAuthUser);

  // User from the db, we will lazy reload when the Cognito sub changes (when user logs in/out).
  const [getDbUser, { client, data: userDbData, loading: dbDataLoading }] =
    useUserAuthContextLazyQuery();

  const setSelectedLocation = React.useCallback(
    (selectedLocation: AuthContextLocation) => {
      if (typeof window === 'undefined') return;

      window.localStorage.setItem('selectedLocation', selectedLocation?.id);
      setAuthUser((prevState) => ({ ...prevState, selectedLocation }));

      // Kick user back to users list page if they switch
      // locations while on the user details page.
      const currentPage = window.location.pathname.split('/')?.[2];
      if (selectedLocation?.id && currentPage === 'user') {
        // replace route on nav stack so user can't just back navigate
        navigate('/app/users', { replace: true });
      }

      setAuthUser((prevState) => ({ ...prevState, selectedLocation }));
    },
    [navigate, window],
  );

  /**
   * Entrypoint for any changes to the authUser.
   *
   * Immediately update the authuser state, and refetch from the DB as needed.
   *
   * @param isNew true if this is a complete greenfield authuser
   * @param newProperties all the key-value pairs that are to be changed in authUser
   */
  const updateAuthUser = React.useCallback(
    (newProperties: Partial<AuthUser>, isNew: boolean) => {
      console.log('update called with', newProperties, isNew);

      setAuthUser((prevState) => {
        const base = isNew ? defaultAuthUser : prevState;
        const isNewCognitoUser =
          isNew || Boolean(newProperties.sub && newProperties.sub != base.sub);

        const newUser = { ...base, setSelectedLocation, ...newProperties };

        console.log('Setting authUser with', newUser);
        setAuthUser(newUser);

        // If a new user, or a known user but we have not yet loaded DB information, reload it from the db.
        // lastName comes from the DB, so use that as indicator of whether we have loaded yet.
        if (newUser.sub && (isNewCognitoUser || !newUser.lastName)) {
          getDbUser({ variables: { _eq: newUser.sub } });
        }

        return newUser;
      });
    },
    [getDbUser, setSelectedLocation],
  );

  /**
   * Get all fresh Cognito data (no use of cache) for current Cognito authentication state.
   *
   * This function is designed for deliberate triggering when we know there is a change.  It should not
   * be called when there is Cogito activity without user related change (such as on refresh token exchange).
   *
   * Auth related data from the DB is refreshed separately as needed as a side effect.
   */
  async function refreshAuthUserFromCognito(
    replaceUser = true,
  ): Promise<AuthUser | void> {
    return Auth.currentAuthenticatedUser({ bypassCache: true })
      .then((user) => {
        const authData = user.signInUserSession;
        const newUser = {
          ...user.attributes,
          authData,
          username: user.username,
          authorized: authData?.accessToken?.jwtToken != undefined,
        };
        updateAuthUser(newUser, replaceUser);
        resetAuth();
        return newUser;
      })
      .catch((err) => {
        console.log('err', err);
      });
  }

  function authUserPromise(): Promise<AuthUser> {
    if (authUser.authorized) {
      return Promise.resolve(authUser);
    }

    const cognitoResult = Auth.currentAuthenticatedUser({
      bypassCache: false,
    }).then((user) => {
      const authData = user.signInUserSession;
      const newUser = {
        ...user.attributes,
        authData,
        username: user.username,
        authorized: authData?.accessToken?.jwtToken != undefined,
      };
      return newUser;
    });
    // Allow err to propagate (which will happen if user is not logged in)

    return cognitoResult;
  }

  // One time get current auth state and subscribe to auth events on start
  useEffect(() => {
    //  Load the current user that Amplify is persisting
    refreshAuthUserFromCognito()
      .then((user) => console.log('User back from refreshAuthUser', user))
      .catch((err) => console.log('Error back from refreshAuthUser', err));

    // Actually subscribe to the events
    Hub.listen('auth', authListener);
    Hub.listen(customAuthChannel, customAuthListener);

    // Cleanup is to unsubscribe from Hub
    return () => {
      Hub.remove('auth', authListener);
      Hub.remove(customAuthChannel, customAuthListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update DB user data in the Context when it has changed (Apollo has reloaded)
  useEffect(() => {
    console.log(
      'In user DB useEffect',
      userDbData,
      dbDataLoading,
      authUser?.sub,
    );
    if (userDbData && !dbDataLoading) {
      console.log('authUser loaded from DB in useEffect with', userDbData);
      if (userDbData.user[0]?.cognitoUserSub) {
        const dbUser = getFormattedUser(userDbData);
        console.log('Got DB user', dbUser);
        if (dbUser) {
          // Much better UI to have a selected location, so default to the first one
          // when there is one to deault to and there is none already selected.
          if (dbUser.locations.length > 0 && !authUser.selectedLocation) {
            console.log('setting selected');

            const savedLocationId =
              typeof window !== 'undefined'
                ? window.localStorage.getItem('selectedLocation')
                : null;
            const selectedLocation =
              dbUser.locations.find((loc) => loc.id === savedLocationId) ??
              dbUser.locations[0];

            updateAuthUser({ ...dbUser, selectedLocation }, false);
          } else {
            updateAuthUser(dbUser, false);
            console.log(
              'not setting selected because',
              dbUser.locations.length,
              authUser.selectedLocation,
            );
          }
        }
      }
    }
  }, [
    authUser.selectedLocation,
    authUser?.sub,
    dbDataLoading,
    updateAuthUser,
    userDbData,
    window,
  ]);

  // Lister for our custom auth events - we only want 1 version of this function as the
  // listening on Hub is constant.
  const customAuthListener = React.useCallback((event: HubCapsule) => {
    const { payload } = event;
    console.log('Hub payload', payload);

    switch (payload.event) {
      // Both of these events are a change in user state in Cognito.  We know what they are so we could
      // patch our data locally, but safest and easiest is just to pull down the Truth and align to it.
      case CustomAuthEvent.SIGNUP_CONFIRM:
        refreshAuthUserFromCognito()
          .then((result) => console.log(result))
          .catch((err) => console.log(err));
        break;
      case CustomAuthEvent.EMAIL_CONFIRM:
        refreshAuthUserFromCognito(false)
          .then((result) => console.log(result))
          .catch((err) => console.log(err));
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lister for Cognito built in auth events - we only want 1 version of this function as the
  // listening on Hub is constant.
  const authListener = React.useCallback((event: HubCapsule) => {
    const { payload } = event;
    console.log('Hub payload', payload);

    switch (payload.event) {
      case AuthEvent.TOKEN_REFRESH_FAILURE:
        // Refresh failure so we don't have an auth user
        updateAuthUser({}, true);
        break;
      case AuthEvent.SIGNIN:
        refreshAuthUserFromCognito().then(() => {
          if (!authUser.email_verified) {
            // Cognito always uses phone verification for account validation, so with phone confirmed
            // (since signed up if got here) it is time to start the email verification process.
            // actually, don't do this....
            /*
            sendEmailVerificationCode()
              .then((result) => console.log(result))
              .catch((err) => console.log(err));
            */
          }
        });
        break;
      case AuthEvent.SIGNOUT:
        updateAuthUser({}, true);
        break;
      case AuthEvent.SIGNUP:
        console.log('signup', payload.data);
        // authUser needs the username right away since the verify code component gets it from the authprovider, and it
        // must be present in the submit code call to Cognito.
        updateAuthUser(
          {
            ...authUser,
            username: payload.data.user.username,
          },
          false,
        );
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = React.useCallback(
    async (goHome = false): Promise<void> => {
      await signout();
      await client?.clearStore();
      updateAuthUser({}, true);

      if (goHome) {
        navigate('/');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client],
  );

  const setUsername = (username: string) => {
    updateAuthUser({ username }, true);
  };

  return (
    <AuthContext.Provider
      value={{
        authUser,
        authUserPromise,
        logout,
        setUsername,
        refresh: async () => {
          await refreshAuthUserFromCognito(true);
          return Promise.resolve();
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
